// The frame around the four tabs (G1-G5 of APP-PARITY.md): header chip, standby button, alarm, the restart bar, the
// message after a command (toast), the read-only note while offline. Tabs and hashes as the local app.
import type { CommandInput, CommandOutcome } from '../commands.js';
import { agoText, reasonText, t, type Lang, type TextKey } from '../i18n.js';
import { hasData, presenceOf, type Presence } from '../calm.js';
import { alarmOf, chipOf, opOf, type Tone } from '../status.js';
import { actionView, textOf, type ActionView, type ScreenContext } from './context.js';
import { formatNumber } from './format.js';
import { readyBand, readyTone } from './ready.js';

/** The four tabs of a paired machine (ids of the shell, ui/tabs.tsx). */
export type TabId = 'maschine' | 'bezug' | 'einstellungen' | 'wartung';

export const TABS: readonly TabId[] = ['maschine', 'bezug', 'einstellungen', 'wartung'];

/** Hashes of the local app (#status, #brew, #settings, #care) keep working as links. */
const LOCAL_HASHES: Record<string, TabId> = {
  status: 'maschine',
  brew: 'bezug',
  settings: 'einstellungen',
  care: 'wartung',
};

/** "#bezug" (or the local app's "#brew"), also "#bezug/verlauf" -> bezug; anything else is the machine tab. */
export const tabOfHash = (hash: string): TabId => {
  const id = hash.replace(/^#/, '').split('/')[0] ?? '';
  if ((TABS as readonly string[]).includes(id)) return id as TabId;
  return LOCAL_HASHES[id] ?? 'maschine';
};

/**
 * Sub-pages with a back arrow (UX line 19.10.2026: what is used seldom sits one level deeper, each page an entry in the
 * history with its own address). Bezug: the recipe editor, one shot, the presets editor, the grinder, the bean library,
 * one bean, the history and the comparison of 2-3 shots (#bezug/rezept, #bezug/bezug/<id>, #bezug/schnellwahl,
 * #bezug/muehle, #bezug/bohnen[/<id>|/neu], #bezug/verlauf, #bezug/vergleich/<id>,<id>[,<id>]). Einstellungen: schedule,
 * scale, water tank, display, Claude, advanced, system with everything about the device (#einstellungen/zeitplan …).
 * Wartung: descaling and the reminders (#wartung/entkalken, #wartung/einstellungen).
 */
export type BrewPage =
  | { page: 'main' }
  | { page: 'recipe' }
  | { page: 'fine' }
  | { page: 'shot'; id: string }
  | { page: 'presets' }
  | { page: 'preset'; index: number }
  | { page: 'grinder' }
  | { page: 'beans' }
  | { page: 'newBean' }
  | { page: 'bean'; id: string }
  | { page: 'history' }
  | { page: 'compare'; ids: string[] };

export type SettingsPage =
  | { page: 'main' }
  | { page: 'schedule' }
  | { page: 'scale' }
  | { page: 'tank' }
  | { page: 'display' }
  | { page: 'claude' }
  | { page: 'advanced' }
  | { page: 'system' };

export type CarePage = { page: 'main' } | { page: 'descale' } | { page: 'settings' };

/** The page shown in a tab; the machine tab has none of its own. */
export type SubPage = BrewPage | SettingsPage | CarePage;

const ID = /^[0-9a-f-]{36}$/;

const SETTINGS_SLUGS = {
  schedule: 'zeitplan',
  scale: 'waage',
  tank: 'wassertank',
  display: 'display',
  claude: 'claude',
  advanced: 'fortgeschritten',
  system: 'system',
} as const;

const BREW_SLUGS = {
  recipe: 'rezept',
  fine: 'feineinstellung',
  presets: 'schnellwahl',
  grinder: 'muehle',
  history: 'verlauf',
} as const;

export function brewPageOfHash(hash: string): BrewPage {
  const [tab, sub, arg] = hash.replace(/^#/, '').split('/');
  if (tab !== 'bezug') return { page: 'main' };
  if (sub === 'bohnen')
    return arg === 'neu'
      ? { page: 'newBean' }
      : arg && ID.test(arg)
        ? { page: 'bean', id: arg }
        : { page: 'beans' };
  if (sub === 'bezug') return arg && ID.test(arg) ? { page: 'shot', id: arg } : { page: 'main' };
  if (sub === 'schnellwahl' && arg && /^[0-2]$/.test(arg)) return { page: 'preset', index: Number(arg) };
  if (sub === 'vergleich') {
    const ids = (arg ?? '')
      .split(',')
      .filter((x) => ID.test(x))
      .slice(0, 3);
    return ids.length >= 2 ? { page: 'compare', ids } : { page: 'history' };
  }
  const named = (Object.keys(BREW_SLUGS) as (keyof typeof BREW_SLUGS)[]).find((k) => BREW_SLUGS[k] === sub);
  return named ? { page: named } : { page: 'main' };
}

export function hashOfBrewPage(p: BrewPage): string {
  switch (p.page) {
    case 'main':
      return '#bezug';
    case 'beans':
      return '#bezug/bohnen';
    case 'newBean':
      return '#bezug/bohnen/neu';
    case 'bean':
      return '#bezug/bohnen/' + p.id;
    case 'shot':
      return '#bezug/bezug/' + p.id;
    case 'compare':
      return '#bezug/vergleich/' + p.ids.join(',');
    case 'preset':
      return '#bezug/schnellwahl/' + p.index;
    default:
      return '#bezug/' + BREW_SLUGS[p.page];
  }
}

export function settingsPageOfHash(hash: string): SettingsPage {
  const [tab, sub] = hash.replace(/^#/, '').split('/');
  if (tab !== 'einstellungen' && tab !== 'settings') return { page: 'main' };
  const named = (Object.keys(SETTINGS_SLUGS) as (keyof typeof SETTINGS_SLUGS)[]).find(
    (k) => SETTINGS_SLUGS[k] === sub,
  );
  return named ? { page: named } : { page: 'main' };
}

export const hashOfSettingsPage = (p: SettingsPage): string =>
  p.page === 'main' ? '#einstellungen' : `#einstellungen/${SETTINGS_SLUGS[p.page]}`;

const CARE_SLUGS = { descale: 'entkalken', settings: 'einstellungen' } as const;

export function carePageOfHash(hash: string): CarePage {
  const [tab, sub] = hash.replace(/^#/, '').split('/');
  if (tab !== 'wartung' && tab !== 'care') return { page: 'main' };
  const named = (Object.keys(CARE_SLUGS) as (keyof typeof CARE_SLUGS)[]).find((k) => CARE_SLUGS[k] === sub);
  return named ? { page: named } : { page: 'main' };
}

export const hashOfCarePage = (p: CarePage): string =>
  p.page === 'main' ? '#wartung' : `#wartung/${CARE_SLUGS[p.page]}`;

/** The page of the address: the tab's own (#bezug/rezept), the main page for anything else. */
export function pageOfHash(hash: string): SubPage {
  const tab = tabOfHash(hash);
  if (tab === 'bezug') return brewPageOfHash(hash);
  if (tab === 'einstellungen') return settingsPageOfHash(hash);
  if (tab === 'wartung') return carePageOfHash(hash);
  return { page: 'main' };
}

/** The address of a page in its tab. */
export function hashOfPage(tab: TabId, p: SubPage): string {
  if (p.page === 'main') return `#${tab}`;
  if (tab === 'bezug') return hashOfBrewPage(p as BrewPage);
  if (tab === 'einstellungen') return hashOfSettingsPage(p as SettingsPage);
  if (tab === 'wartung') return hashOfCarePage(p as CarePage);
  return `#${tab}`;
}

/** Where the back arrow leads when the page was opened directly (a link, a reload). */
export function parentOf(p: SubPage): SubPage {
  if (p.page === 'bean' || p.page === 'newBean') return { page: 'beans' };
  if (p.page === 'compare') return { page: 'history' };
  if (p.page === 'fine') return { page: 'recipe' };
  if (p.page === 'preset') return { page: 'presets' };
  return { page: 'main' };
}

export function chipView(ctx: ScreenContext): { text: string; tone: Tone } {
  const pi =
    ctx.value('brew.pre_infusion.enabled') === true
      ? ctx.num('brew.pre_infusion.time') + ctx.num('brew.pre_infusion.pause')
      : 0;
  // ready as on the ring of "Maschine" and on the display (screens/ready.ts)
  const chip = chipOf(ctx.view, ctx.now, { piSeconds: pi, readyBand: readyBand(ctx), tone: readyTone(ctx) });
  // the running seconds with one decimal in the screen's language ("Bezug 3,0 s", 22.10.2026: it said "0.0")
  const vars =
    chip.vars && typeof chip.vars.s === 'number'
      ? { ...chip.vars, s: formatNumber(chip.vars.s, 1, ctx.lang) }
      : chip.vars;
  return { text: textOf(ctx, chip.key, vars), tone: chip.tone };
}

/** The on/off button in the header: standby now, or wake (a wake is class water_heat_wake on the machine). */
export function standbyButton(ctx: ScreenContext): ActionView & { standby: boolean } {
  const standby = opOf(ctx.view).state === 80;
  return {
    ...actionView(ctx, 'standby.set', { label: standby ? 'wakeUp' : 'toStandby', args: { on: !standby } }),
    standby,
  };
}

export function alarmView(ctx: ScreenContext): { text: string; hint: string; tone: 'error' | 'info' } | null {
  const a = alarmOf(ctx.view, ctx.now);
  return a ? { text: ctx.tr(a.key), hint: textOf(ctx, `${a.key}Hint`), tone: a.tone } : null;
}

/** "Wirkt nach einem Neustart – Jetzt neu starten" while a state group reports pending values (G4). */
export function restartBar(
  ctx: ScreenContext,
): { text: string; pending: string[]; restart: ActionView } | null {
  const pending = Object.values(ctx.view.groups).flatMap((g) => {
    const p = (g.data as { pending?: unknown }).pending;
    return Array.isArray(p) ? p.filter((x): x is string => typeof x === 'string') : [];
  });
  if (!pending.length) return null;
  return {
    text: ctx.tr('restartBar'),
    pending,
    // "Neu starten" fits the bar in one line (23.10.2026: "Jetzt neu starten" wrapped)
    restart: actionView(ctx, 'restart', {
      label: 'restart',
      ask: { title: 'restartQ', text: 'restartText' },
    }),
  };
}

/**
 * Why nothing can be changed right now, and when that changes (FR-14, FR-29), or null. Only for states that last
 * (core/calm.ts): a short drop of the machine or of this device shows nothing. The emergency stop has its own banner.
 */
export function readOnlyNote(ctx: ScreenContext): string | null {
  const v = ctx.view;
  if (ctx.controllable || !hasData(v)) return null;
  if (v.accessEnded) return ctx.tr('readOnlyRevoked');
  switch (presenceOf(v, ctx.now)) {
    case 'link_down':
      return ctx.tr('readOnlyLink', { t: clockText(ctx.lang, v.updatedAt) });
    case 'offline':
      return ctx.tr('readOnlyOffline', { ago: agoText(ctx.lang, v.conn.lastSeenAt, ctx.now) });
    case 'restarting':
      return ctx.tr('readOnlyRestarting');
    case 'unknown':
      return ctx.tr('readOnlyUnknown');
    default:
      return null;
  }
}

/**
 * The connection line under the machine's name (calm, core/calm.ts): "online", "verbindet neu …", "offline · zuletzt
 * gesehen vor 25 min", "Status unbekannt" while this device has no connection (the cloud bar says why, the note
 * since when), "verbinde …" before the first snapshot.
 */
export function connLine(ctx: ScreenContext): {
  presence: Presence | 'loading';
  text: string;
  tone: 'ok' | 'warn' | 'danger' | 'neutral';
} {
  const v = ctx.view;
  if (!hasData(v)) return { presence: 'loading', text: ctx.tr('conn_loading'), tone: 'neutral' };
  const presence = presenceOf(v, ctx.now);
  switch (presence) {
    case 'online':
      return { presence, text: ctx.tr('conn_online'), tone: 'ok' };
    case 'reconnecting':
      return { presence, text: ctx.tr('conn_reconnecting'), tone: 'warn' };
    case 'restarting':
      return { presence, text: ctx.tr('conn_restarting'), tone: 'warn' };
    case 'offline':
      return {
        presence,
        text: `${ctx.tr('conn_offline')} · ${ctx.tr('lastSeen')} ${agoText(ctx.lang, v.conn.lastSeenAt, ctx.now)}`,
        tone: 'danger',
      };
    case 'unknown':
      return { presence, text: ctx.tr('conn_unknown'), tone: 'neutral' };
    case 'link_down':
      return { presence, text: ctx.tr('conn_link_down'), tone: 'neutral' };
  }
}

/** "21:24" in the app's language. */
export const clockText = (lang: Lang, ms: number): string =>
  new Date(ms).toLocaleTimeString(lang === 'de' ? 'de-DE' : 'en-GB', { hour: '2-digit', minute: '2-digit' });

/**
 * The message after a command (G5, FR-20). Success is quiet (Dominik, 19.10.2026: the "Gespeichert" bar came too
 * often): the value loses its provisional look once the machine confirms it, an action shows in the state (STANDBY,
 * SPÜLEN). Only what the user must know comes as a message: "Wirkt nach dem Bezug/einem Neustart" (info), and every
 * failure in plain words with what to do (error); the value has already gone back to the machine's (FR-21).
 */
export function toastOf(
  lang: Lang,
  input: CommandInput,
  outcome: CommandOutcome,
): { text: string; tone: 'info' | 'error' } | null {
  const tr = (key: TextKey) => t(lang, key);
  const error = (text: string) => ({ text, tone: 'error' as const });
  switch (outcome.result) {
    case 'done':
      if (outcome.applies === 'after_brew') return { text: tr('savedAfterBrew'), tone: 'info' };
      if (outcome.applies === 'after_restart') return { text: tr('savedAfterRestart'), tone: 'info' };
      return null;
    case 'unconfirmed':
      // not sent at all (this device has no connection, core/commands.ts) vs. sent but the answer was lost
      return error(tr(outcome.reason === 'offline' ? 'notSentLink' : 'notConfirmed'));
    case 'no_response':
      return error(tr('noResponse'));
    case 'expired':
      return error(tr('expiredToast'));
    case 'stale':
      return error(tr('staleToast'));
    default:
      break;
  }
  if (outcome.reason === 'fresh_login') return error(tr('freshLogin'));
  // the API refuses commands to a machine that is not connected (AD-12)
  if (outcome.reason === 'offline') return error(tr('notSentMachine'));
  const head = tr(input.kind === 'set' ? 'failedToast' : 'failedActionToast');
  const why = outcome.reason ? reasonText(lang, outcome.reason) : '';
  // head and reason as sentences, no dash chain ("Nicht ausgeführt. Der Wassertank ist leer. Füll Wasser nach.")
  return error(why ? `${head}. ${why}` : head);
}
